import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog'
import { Button } from '../components/ui/button'
import { useEffect, useState } from 'react'
import { Endpoints } from '@/remote/endpoints'
import { usePromiseToast } from '../hooks/toast-promisse'
import { useRemoteClientGet } from '@/configs/http-client'
import { convertDateDisplayFormat } from '../utils/formatDateDisplay'
import { formatToBRL } from '../utils/formatCurrency'
import { PlanFormData } from '../validators/plan-validator'
import { useSearchParams } from 'react-router-dom'

type DialogProps = {
  id: number
  isOpen: boolean
  closeDialog: () => void
}

export function DialogDetailPlans({ id, isOpen = false, closeDialog }: DialogProps) {
  const { remoteClientGet } = useRemoteClientGet();
  const { triggerToast } = usePromiseToast();
  const [params] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<PlanFormData>();

  const plansFields = [
    { label: 'Nome', value: data?.nome },
    { label: 'Descrição', value: data?.descricao },
    { label: 'Unidade', value: data?.unidade?.nomeFantasia },
    // { label: 'Centro de Custo', value: data?.centroCusto?.id },
    { label: 'Data de Início', value: data?.dataInicio, isDate: true },
    { label: 'Data de Vencimento', value: data?.dataVencimento, isDate: true },
    { label: 'Venda Online', value: data?.vendaOnline ? 'Sim' : 'Não' },
    { label: 'Valor', value: formatToBRL(data?.valor || "0") },
    { label: 'Máximo de Parcelas', value: data?.maximoParcelas },
    { label: 'Comissão Percentual', value: data?.comissaoPercentual ? 'Sim' : 'Não' },
    { label: 'Valor da Comissão', value: formatToBRL(data?.valorComissao || "0") },
    { label: 'Valor de Custo', value: formatToBRL(data?.valorCusto || "0") },
    { label: 'Quantidade', value: data?.quantidade },
    { label: 'Valor de Desconto', value: formatToBRL(data?.valorDesconto || "0") }
  ];


  const fetchData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await remoteClientGet(`${Endpoints.plans}/${id}?unidadeId=${params.get('unit')}`);
      setData(response?.data?.data);
    } catch (error) {
      setError('Erro ao buscar dados do plano');
      console.error(error);
      throw error
    } finally {
      setIsLoading(false);
    }
  };

  const formatFieldValue = (field: { value: any, isDate?: boolean, isCurrency?: boolean }) => {
    if (!field.value) return "";
    if (field.isDate) return convertDateDisplayFormat(field.value.split('T')[0]);
    if (field.isCurrency) return formatToBRL(field.value);
    return field.value;
  };

  useEffect(() => {
    if (isOpen) {
      triggerToast(fetchData());
    }
  }, [isOpen, id]);

  const renderField = (field: any, index: number) => (
    <p key={index} className="text-gray-400 flex gap-1">
      <strong className="text-acelerafit-gray-dark">{field.label}:</strong>
      {formatFieldValue(field)}
    </p>
  );

  return (
    <Dialog open={isOpen}>
      <DialogContent className="sm:max-w-4xl">
        <DialogHeader className="border-acelerafit-gray-dark">
          <DialogTitle className="text-acelerafit-green-300">Planos</DialogTitle>
          <DialogDescription>Detalhes</DialogDescription>
        </DialogHeader>

        {isLoading && (
          <div className="flex justify-center items-center p-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-acelerafit-green-300" />
          </div>
        )}

        {error && (
          <div className="text-red-500 p-4 text-center">
            {error}
          </div>
        )}

        {!isLoading && !error && (
          <div className="grid grid-cols-2 col-span-1 gap-4">
            {plansFields.map((field, index) => renderField(field, index))}
          </div>
        )}

        <DialogFooter className="sm:justify-end">
          <Button
            onClick={() => closeDialog()}
            className="bg-acelerafit-green-300 shadow-2xl hover:bg-acelerafit-green-300 hover:opacity-85 transition-all"
          >
            Fechar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}